import { useState } from 'react';
import { Banner, Button, Card, compareMoney, Dialog, Field, Money, PageHeader, Pill } from '@elysian/ui';
import type { StatementSchedule } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { iso, shortDate } from '../invoices/shared';
import { ExportMenu } from '../shared/ExportMenu';
import { contactColumns } from '../shared/exportColumns';

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export function describeSchedule(s: StatementSchedule): string {
  const when = s.frequency === 'weekly'
    ? `Every ${WEEKDAYS[(s.weekday ?? 1) - 1]}`
    : s.day_of_month === 0 || s.day_of_month == null ? 'On the last day of each month' : `On the ${ordinal(s.day_of_month)} of each month`;
  return `${when}, ${s.type === 'activity' ? 'all activity' : 'open items'}${s.only_overdue ? ', only customers with something overdue' : ''}`;
}

function ordinal(n: number) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
}

export function Statements() {
  const { source, today } = useSession();
  const toast = useToast((s) => s.show);
  const [version, setVersion] = useState(0);
  const data = useAsync(async () => {
    const [customers, schedules] = await Promise.all([source.contacts('customer'), source.statementSchedules()]);
    return { customers: customers.filter((c) => compareMoney(c.balance, '0') > 0), schedules };
  }, [source, version], { keep: true });
  const [asAt, setAsAt] = useState(iso(today()));
  const [type, setType] = useState<'open_items' | 'activity'>('open_items');
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const customers = data.state === 'ready' ? data.data.customers : [];
  const chosen = customers.filter((c) => picked[c.id] ?? true);

  async function make(send: boolean) {
    setBusy(true);
    setError(null);
    try {
      const r = await source.createStatements({ contact_ids: chosen.map((c) => c.id), as_at: asAt, type, send, only_overdue: onlyOverdue });
      const parts = [`${r.created} ${r.created === 1 ? 'statement' : 'statements'} ${send ? 'made' : 'ready to download'}`];
      if (send) parts.push(`${r.emailed ?? 0} emailed`);
      if (r.skipped) parts.push(`${r.skipped} skipped (nothing to show)`);
      if (r.no_email?.length) parts.push(`no email address for ${r.no_email.join(', ')}`);
      toast(parts.join(' · '));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader title="Statements" subtitle="What each customer owes, sent now or on a schedule" />
      {data.state === 'error' && <Banner>Couldn’t load: {data.error.message}</Banner>}
      <div className="el-two-col" data-wide="right">
        <Card as="section" padding="18px 20px" aria-labelledby="st-now">
          <h2 id="st-now" className="el-h2" style={{ marginBottom: 12 }}>Send statements now</h2>
          <div className="el-stack" style={{ gap: 12 }}>
            <Field id="st-asat" label="As at">
              <input id="st-asat" type="date" className="el-input" value={asAt} onChange={(e) => setAsAt(e.target.value)} />
            </Field>
            <Field id="st-type" label="Show">
              <select id="st-type" className="el-input" value={type} onChange={(e) => setType(e.target.value as typeof type)}>
                <option value="open_items">Open items: unpaid invoices and credits</option>
                <option value="activity">Activity: every invoice and payment this month</option>
              </select>
            </Field>
            <label className="el-check"><input type="checkbox" checked={onlyOverdue} onChange={(e) => setOnlyOverdue(e.target.checked)} /> Only customers with something overdue</label>
            {error && <div className="el-error-box" role="alert">{error}</div>}
            <div className="el-row">
              <Button disabled={busy || !chosen.length} onClick={() => void make(false)}>Make PDFs</Button>
              <Button variant="primary" disabled={busy || !chosen.length} onClick={() => void make(true)}>Email {chosen.length} {chosen.length === 1 ? 'customer' : 'customers'}</Button>
            </div>
          </div>
        </Card>

        <Card as="section" padding="18px 0 8px" aria-labelledby="st-who">
          <div className="el-card-head">
            <h2 id="st-who" className="el-h2">Customers with a balance</h2>
            <span className="el-row" style={{ gap: 12, alignItems: 'center' }}>
              <span className="el-hint">{chosen.length} of {customers.length} chosen</span>
              <ExportMenu name="Customer balances" columns={contactColumns('customer')} rows={customers} disabled={data.state !== 'ready'} />
            </span>
          </div>
          <div className="el-table-wrap">
            <table className="el-table" aria-label="Customers with a balance">
              <tbody>
                {customers.map((c) => (
                  <tr key={c.id}>
                    <td style={{ width: 40 }}><input type="checkbox" aria-label={`Include ${c.name}`} checked={picked[c.id] ?? true} onChange={(e) => setPicked({ ...picked, [c.id]: e.target.checked })} /></td>
                    <td>{c.name}<div className="el-hint">{c.email ?? 'No email address'}</div></td>
                    <td className="el-num"><Money value={c.balance} /></td>
                  </tr>
                ))}
                {data.state === 'ready' && customers.length === 0 && <tr><td className="el-muted">No customer owes anything.</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <Card as="section" padding="18px 0 8px" aria-labelledby="st-sched" className="el-mt">
        <div className="el-card-head">
          <h2 id="st-sched" className="el-h2">Schedules</h2>
          <Button size="sm" onClick={() => setAdding(true)}>Add a schedule</Button>
        </div>
        <div className="el-table-wrap">
          <table className="el-table" aria-label="Statement schedules">
            <tbody>
              {(data.state === 'ready' ? data.data.schedules : []).map((s) => (
                <tr key={s.id}>
                  <td><strong>{s.name}</strong><div className="el-hint">{describeSchedule(s)}</div></td>
                  <td>{s.is_active ? <Pill tone="ok">On</Pill> : <Pill tone="draft">Paused</Pill>}</td>
                  <td className="el-num">{s.next_run_date && <>Next {shortDate(s.next_run_date)}</>}</td>
                </tr>
              ))}
              {data.state === 'ready' && data.data.schedules.length === 0 && <tr><td className="el-muted">No schedules. Statements go only when you send them.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {adding && <AddSchedule onClose={() => setAdding(false)} onSaved={(s) => { setAdding(false); toast(`${s.name} saved. Next run ${shortDate(s.next_run_date)}`); setVersion((v) => v + 1); }} />}
    </>
  );
}

function AddSchedule({ onClose, onSaved }: { onClose: () => void; onSaved: (s: StatementSchedule) => void }) {
  const { source } = useSession();
  const [name, setName] = useState('Month-end statements');
  const [frequency, setFrequency] = useState<'monthly' | 'weekly'>('monthly');
  const [day, setDay] = useState(0);
  const [weekday, setWeekday] = useState(1);
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function save() {
    setBusy(true);
    setError(null);
    try {
      onSaved(await source.createStatementSchedule({ name, frequency, type: 'open_items', day_of_month: frequency === 'monthly' ? day : null,
        weekday: frequency === 'weekly' ? weekday : null, only_overdue: onlyOverdue }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }
  return (
    <Dialog title="Add a statement schedule" onClose={onClose}
      actions={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" disabled={busy || !name.trim()} onClick={() => void save()}>Save schedule</Button></>}>
      <Field id="sc-name" label="Name"><input id="sc-name" className="el-input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
      <Field id="sc-freq" label="How often">
        <select id="sc-freq" className="el-input" value={frequency} onChange={(e) => setFrequency(e.target.value as typeof frequency)}>
          <option value="monthly">Monthly</option><option value="weekly">Weekly</option>
        </select>
      </Field>
      {frequency === 'monthly' ? (
        <Field id="sc-day" label="Day">
          <select id="sc-day" className="el-input" value={day} onChange={(e) => setDay(Number(e.target.value))}>
            <option value={0}>Last day of the month</option>
            {Array.from({ length: 28 }, (_, i) => <option key={i + 1} value={i + 1}>{ordinal(i + 1)}</option>)}
          </select>
        </Field>
      ) : (
        <Field id="sc-wd" label="Day">
          <select id="sc-wd" className="el-input" value={weekday} onChange={(e) => setWeekday(Number(e.target.value))}>
            {WEEKDAYS.map((w, i) => <option key={w} value={i + 1}>{w}</option>)}
          </select>
        </Field>
      )}
      <label className="el-check"><input type="checkbox" checked={onlyOverdue} onChange={(e) => setOnlyOverdue(e.target.checked)} /> Only customers with something overdue</label>
      {error && <div className="el-error-box" role="alert">{error}</div>}
    </Dialog>
  );
}
